import React, { useState, useMemo, useEffect } from "react";
import { Card, Table, Row, Col, Form, Button, Dropdown, Badge, Modal, Spinner, Toast, ToastContainer, OverlayTrigger, Tooltip } from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import { fetchAlerteMarchand } from "../services/api";

// ============================================
// UTILITAIRES
// ============================================
const getNotificationIcon = (type) => {
  switch (type) {
    case 'success': return 'bi-check-circle-fill';
    case 'error': return 'bi-exclamation-circle-fill';
    case 'warning': return 'bi-exclamation-triangle-fill';
    case 'info': return 'bi-info-circle-fill';
    default: return 'bi-bell-fill';
  }
};

const getNotificationBg = (type) => {
  switch (type) {
    case 'success': return 'success';
    case 'error': return 'danger';
    case 'warning': return 'warning';
    case 'info': return 'info';
    default: return 'primary';
  }
};

const getAlertIcon = (type) => {
  switch (type) {
    case 'success': return 'bi-check-circle';
    case 'danger': return 'bi-exclamation-triangle';
    case 'warning': return 'bi-exclamation-triangle';
    case 'info': return 'bi-info-circle';
    default: return 'bi-bell';
  }
};

export default function SeuilsAlerteManagement() {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedSeuils, setSelectedSeuils] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const [error, setError] = useState("");
  const [seuils, setSeuils] = useState([]);
  const [loadingSeuils, setLoadingSeuils] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  const [selectedSeuil, setSelectedSeuil] = useState(null);
  const [editSeuil, setEditSeuil] = useState({
    seuil: '',
    type_notification: 'email',
    actif: 1
  });
  const [newSeuil, setNewSeuil] = useState({
    seuil: '',
    type_notification: 'email',
    actif: 1
  });

  // ============================================
  // NOTIFICATIONS
  // ============================================
  const showNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    setNotifications(prev => [{ id, message, type, show: true }, ...prev]);
    setTimeout(() => hideNotification(id), duration);
  };

  const hideNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const showAlert = (message, type = 'warning', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) setTimeout(() => hideAlert(), duration);
  };

  const hideAlert = () => {
    setAlertMessage({ show: false, message: '', type: 'info' });
  };

  // ============================================
  // CHARGEMENT
  // ============================================
  useEffect(() => {
    loadSeuils();
  }, []);

  const loadSeuils = async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) setRefreshing(true);
      else setLoadingSeuils(true);
      setError("");

      const token = localStorage.getItem('authToken');
      if (!token) {
        setError("Token d'authentification non trouvé");
        showAlert("Token d'authentification non trouvé. Veuillez vous reconnecter.", 'danger');
        return;
      }

      const seuilsData = await fetchAlerteMarchand(token);

      if (!seuilsData || !seuilsData.data || !Array.isArray(seuilsData.data)) {
        setError("Format de données invalide reçu du serveur");
        showAlert("Format de données invalide reçu du serveur", 'danger');
        return;
      }

      const formattedSeuils = seuilsData.data.map(seuil => ({
        id: seuil.id,
        marchand_id: seuil.marchand_id,
        seuil: seuil.seuil || '0.00',
        type_notification: seuil.type_notification || 'email',
        actif: seuil.actif || 0,
        date_creation: seuil.date_creation || '',
        date_derniere_alerte: seuil.date_derniere_alerte || '',
        created_at: seuil.created_at || '',
        updated_at: seuil.updated_at || ''
      }));

      setSeuils(formattedSeuils);
      if (showRefreshSpinner) {
        showNotification('Liste des seuils actualisée', 'success', 3000);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des seuils:', error);
      const errorMessage = error.message || "Erreur lors du chargement des seuils";
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
    } finally {
      setLoadingSeuils(false);
      setRefreshing(false);
    }
  };

  // ============================================
  // HELPERS
  // ============================================
  const getTypeNotificationLabel = (type) => {
    const types = {
      'email': 'Email',
      'sms': 'SMS',
      'push': 'Notification Push',
      'both': 'Email et SMS'
    };
    return types[type] || type;
  };

  const getTypeNotificationBadge = (type) => {
    const colors = {
      'email': 'primary',
      'sms': 'success',
      'push': 'warning',
      'both': 'info'
    };
    return colors[type] || 'secondary';
  };

  const getTypeNotificationClass = (type) => {
    switch (type) {
      case 'email': return 'se-notif-email';
      case 'sms': return 'se-notif-sms';
      case 'push': return 'se-notif-push';
      case 'both': return 'se-notif-both';
      default: return 'se-notif-default';
    }
  };

  const getTypeNotificationIcon = (type) => {
    switch (type) {
      case 'email': return 'bi-envelope-fill';
      case 'sms': return 'bi-chat-dots-fill';
      case 'push': return 'bi-bell-fill';
      case 'both': return 'bi-send-fill';
      default: return 'bi-bell';
    }
  };

  const getStatutBadge = (actif) => actif === 1 ? 'success' : 'danger';
  const getStatutLabel = (actif) => actif === 1 ? 'Actif' : 'Inactif';
  const getStatutClass = (actif) => actif === 1 ? 'se-status-active' : 'se-status-inactive';

  const handleShowDetails = (seuil) => {
    setSelectedSeuil(seuil);
    setShowDetailsModal(true);
  };

  const handleEditSeuil = (seuil) => {
    setSelectedSeuil(seuil);
    setEditSeuil({
      seuil: seuil.seuil,
      type_notification: seuil.type_notification,
      actif: seuil.actif
    });
    setShowEditModal(true);
  };

  const handleAddSeuil = () => {
    setNewSeuil({ seuil: '', type_notification: 'email', actif: 1 });
    setShowAddModal(true);
  };

  const handleUpdateSeuil = async () => {
    if (!selectedSeuil) return;
    if (!editSeuil.seuil.trim() || isNaN(parseFloat(editSeuil.seuil))) {
      showAlert("Veuillez saisir un seuil valide", 'warning');
      return;
    }

    setUpdateLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      setTimeout(() => {
        setShowEditModal(false);
        setSelectedSeuil(null);
        loadSeuils();
        showNotification(`Seuil ${editSeuil.seuil} mis à jour avec succès`, 'success', 5000);
        setUpdateLoading(false);
      }, 1000);
    } catch (err) {
      console.error('❌ Erreur lors de la mise à jour du seuil:', err);
      showAlert(`Erreur lors de la mise à jour: ${err.message}`, 'error');
      setUpdateLoading(false);
    }
  };

  const handleCreateSeuil = async () => {
    if (!newSeuil.seuil.trim() || isNaN(parseFloat(newSeuil.seuil))) {
      showAlert("Veuillez saisir un seuil valide", 'warning');
      return;
    }

    setCreateLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      setTimeout(() => {
        setShowAddModal(false);
        setNewSeuil({ seuil: '', type_notification: 'email', actif: 1 });
        loadSeuils();
        showNotification(`Nouveau seuil ${newSeuil.seuil} créé avec succès`, 'success', 5000);
        setCreateLoading(false);
      }, 1000);
    } catch (err) {
      console.error('❌ Erreur lors de la création du seuil:', err);
      showAlert(`Erreur lors de la création: ${err.message}`, 'error');
      setCreateLoading(false);
    }
  };

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    let filtered = seuils.filter(item => {
      const searchTerm = filterText.toLowerCase();
      if (!searchTerm.trim()) return true;

      return (
        (item.seuil && item.seuil.includes(searchTerm)) ||
        (item.type_notification && item.type_notification.toLowerCase().includes(searchTerm)) ||
        (getTypeNotificationLabel(item.type_notification).toLowerCase().includes(searchTerm)) ||
        (item.marchand_id && item.marchand_id.toString().includes(searchTerm))
      );
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [seuils, filterText, sortField, sortDirection]);

  // ============================================
  // STATS
  // ============================================
  const stats = useMemo(() => {
    const total = seuils.length;
    const actifs = seuils.filter(s => s.actif === 1).length;
    const inactifs = total - actifs;
    const email = seuils.filter(s => s.type_notification === 'email').length;
    const sms = seuils.filter(s => s.type_notification === 'sms').length;
    const autresTypes = total - email - sms;
    return { total, actifs, inactifs, email, sms, autresTypes };
  }, [seuils]);

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ============================================
  // SÉLECTION
  // ============================================
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedSeuils(currentItems.map(s => s.id));
    else setSelectedSeuils([]);
  };

  const handleSelectSeuil = (seuilId) => {
    setSelectedSeuils(prev =>
      prev.includes(seuilId)
        ? prev.filter(id => id !== seuilId)
        : [...prev, seuilId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedSeuils.length === currentItems.length;
  const isSomeSelected = selectedSeuils.length > 0 && selectedSeuils.length < currentItems.length;

  // ============================================
  // EXPORT
  // ============================================
  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedSeuils.length > 0
      ? seuils.filter(s => selectedSeuils.includes(s.id))
      : filteredAndSortedItems;

    if (!dataToExport.length) {
      showAlert("Aucune donnée à exporter", 'warning', 4000);
      setExportLoading(false);
      return;
    }

    try {
      const enhancedData = dataToExport.map(seuil => ({
        ...seuil,
        type_notification_label: getTypeNotificationLabel(seuil.type_notification),
        statut_label: getStatutLabel(seuil.actif),
        date_creation_formatted: seuil.date_creation ? new Date(seuil.date_creation).toLocaleDateString('fr-FR') : 'N/A',
        date_derniere_alerte_formatted: seuil.date_derniere_alerte ? new Date(seuil.date_derniere_alerte).toLocaleDateString('fr-FR') : 'N/A'
      }));

      if (format === "csv") {
        const headers = ["ID","Marchand ID","Seuil","Type Notification","Statut","Date Création","Dernière Alerte"];
        const csvContent = [
          "\uFEFF" + headers.join(";"),
          ...enhancedData.map(s => [
            s.id, s.marchand_id, s.seuil, s.type_notification_label,
            s.statut_label, s.date_creation_formatted, s.date_derniere_alerte_formatted
          ].map(v => `"${v || ''}"`).join(";"))
        ].join("\n");
        downloadFile(csvContent, "text/csv;charset=utf-8;", `export-seuils-${Date.now()}.csv`);
      } else if (format === "excel") {
        const ws = XLSX.utils.json_to_sheet(enhancedData.map(s => ({
          ID: s.id,
          "Marchand ID": s.marchand_id,
          "Seuil (crédits)": s.seuil,
          "Type Notification": s.type_notification_label,
          "Statut": s.statut_label,
          "Date Création": s.date_creation_formatted,
          "Dernière Alerte": s.date_derniere_alerte_formatted
        })));
        ws['!cols'] = [
          { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 15 },
          { wch: 10 }, { wch: 15 }, { wch: 15 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Seuils Alerte");
        XLSX.writeFile(wb, `export-seuils-${Date.now()}.xlsx`);
      } else if (format === "json") {
        downloadFile(JSON.stringify(enhancedData, null, 2), "application/json;charset=utf-8;", `export-seuils-${Date.now()}.json`);
      } else if (format === "pdf") {
        generatePDF(enhancedData);
      }

      showNotification(`Export ${format.toUpperCase()} réussi - ${dataToExport.length} enregistrement(s)`, 'success', 4000);
    } catch (error) {
      showAlert(`Erreur lors de l'export: ${error.message}`, 'error', 6000);
    } finally {
      setExportLoading(false);
    }
  };

  const generatePDF = (data) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let yPosition = margin;

      const primaryColor = [44, 62, 120];
      const softGray = [245, 246, 248];
      const darkText = [40, 40, 40];
      const borderGray = [210, 214, 220];

      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 30, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text("CONFIGURATION DES SEUILS D'ALERTE", pageWidth / 2, 12, { align: "center" });

      doc.setFontSize(9);
      doc.setFont(undefined, "normal");
      doc.text(`Généré le ${new Date().toLocaleDateString("fr-FR")} • ${data.length} seuil(s) configuré(s)`, pageWidth / 2, 20, { align: "center" });

      yPosition = 40;

      const headers = ["MARCHAND", "SEUIL", "NOTIFICATION", "STATUT", "DATE CRÉATION"];
      const columnWidths = [25, 20, 30, 20, 25];
      const totalWidth = columnWidths.reduce((a, b) => a + b);

      const drawTableHeader = () => {
        doc.setFillColor(...primaryColor);
        doc.rect(margin, yPosition, totalWidth, 9, "F");
        let x = margin;
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont(undefined, "bold");
        headers.forEach((head, i) => {
          doc.text(head, x + 2, yPosition + 6);
          x += columnWidths[i];
        });
        yPosition += 9;
      };

      drawTableHeader();
      doc.setFontSize(7);
      doc.setFont(undefined, "normal");

      data.forEach((row, index) => {
        if (yPosition > 265) {
          doc.addPage();
          yPosition = 20;
          drawTableHeader();
        }
        if (index % 2 === 0) {
          doc.setFillColor(...softGray);
          doc.rect(margin, yPosition, totalWidth, 7, "F");
        }
        let x = margin;
        doc.setTextColor(...darkText);
        const rowData = [
          `M-${row.marchand_id}`,
          `${row.seuil} crédits`,
          row.type_notification_label || "-",
          row.statut_label || "-",
          row.date_creation_formatted || "-"
        ];
        rowData.forEach((cell, i) => {
          let text = cell.toString();
          const limit = [15, 15, 20, 12, 15][i];
          if (text.length > limit) text = text.substring(0, limit - 3) + "...";
          doc.text(text, x + 2, yPosition + 5);
          x += columnWidths[i];
        });
        yPosition += 7;
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(...borderGray);
        doc.line(margin, 283, pageWidth - margin, 283);
        doc.setFontSize(7);
        doc.setTextColor(110, 110, 110);
        doc.text(`Page ${i} / ${pageCount}`, pageWidth / 2, 289, { align: "center" });
      }

      doc.save(`seuils-alerte-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("❌ Erreur PDF:", error);
      throw new Error(`Erreur PDF: ${error.message}`);
    }
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  };

  const SortableHeader = ({ field, children }) => (
    <th className="se-sortable-header" onClick={() => handleSort(field)}>
      <span className="se-sortable-content">
        {children}
        {sortField === field && (
          <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill se-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="se-page main main-app p-3 p-lg-4">
      {/* ========== TOASTS ========== */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        {notifications.map((notification) => {
          const bgColor = getNotificationBg(notification.type);
          const icon = getNotificationIcon(notification.type);
          return (
            <Toast
              key={notification.id}
              show={notification.show}
              onClose={() => hideNotification(notification.id)}
              delay={8000}
              autohide
              bg={bgColor}
            >
              <Toast.Header className={`bg-${bgColor} text-white`}>
                <i className={`bi ${icon} me-2`}></i>
                <strong className="me-auto">
                  {notification.type === 'success' ? 'Succès' :
                   notification.type === 'error' ? 'Erreur' :
                   notification.type === 'warning' ? 'Attention' : 'Information'}
                </strong>
                <small>{new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
              </Toast.Header>
              <Toast.Body className="text-white">{notification.message}</Toast.Body>
            </Toast>
          );
        })}
      </ToastContainer>

      {/* ========== ALERT ========== */}
      {alertMessage.show && (
        <div className={`se-alert se-alert-${alertMessage.type} mb-4`}>
          <i className={`bi ${getAlertIcon(alertMessage.type)}`}></i>
          <div className="flex-grow-1">{alertMessage.message}</div>
          <button className="se-alert-close" onClick={hideAlert}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}

      {/* ========== HEADER ========== */}
      <div className="se-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="se-header-icon">
              <i className="bi bi-sliders2"></i>
            </div>
            <div>
              <h3 className="se-header-title mb-1">Configuration des seuils d'alerte</h3>
              <p className="se-header-subtitle mb-0">
                Gestion des seuils de notification pour les marchands
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Dropdown>
              <Dropdown.Toggle className="se-btn-secondary" disabled={exportLoading}>
                <i className={`bi ${exportLoading ? 'bi-arrow-repeat se-spin' : 'bi-download'}`}></i>
                <span>Exporter</span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="se-dropdown-menu">
                <Dropdown.Item onClick={() => handleExport('csv')}>
                  <i className="bi bi-file-earmark-spreadsheet text-success me-2"></i>Export CSV
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('excel')}>
                  <i className="bi bi-file-earmark-excel text-success me-2"></i>Export Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('pdf')}>
                  <i className="bi bi-file-earmark-pdf text-danger me-2"></i>Export PDF
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('json')}>
                  <i className="bi bi-file-earmark-code text-warning me-2"></i>Export JSON
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Button className="se-btn-primary" onClick={() => loadSeuils(true)} disabled={refreshing}>
              <i className={`bi ${refreshing ? 'bi-arrow-repeat se-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>{refreshing ? 'Actualisation...' : 'Actualiser'}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ========== ERROR ========== */}
      {error && (
        <div className="se-alert se-alert-danger mb-4">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div className="flex-grow-1">{error}</div>
          <Button variant="outline-danger" size="sm" onClick={() => loadSeuils(true)}>
            <i className="bi bi-arrow-clockwise me-1"></i>Réessayer
          </Button>
        </div>
      )}

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={4} lg={2}>
          <div className="se-stat-card">
            <div className="se-stat-icon-wrapper se-stat-icon-blue">
              <i className="bi bi-sliders"></i>
            </div>
            <div className="se-stat-content">
              <div className="se-stat-label">Total seuils</div>
              <div className="se-stat-value">{stats.total}</div>
            </div>
          </div>
        </Col>
        <Col xs={6} md={4} lg={2}>
          <div className="se-stat-card">
            <div className="se-stat-icon-wrapper se-stat-icon-green">
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <div className="se-stat-content">
              <div className="se-stat-label">Actifs</div>
              <div className="se-stat-value">{stats.actifs}</div>
            </div>
          </div>
        </Col>
        <Col xs={6} md={4} lg={2}>
          <div className="se-stat-card">
            <div className="se-stat-icon-wrapper se-stat-icon-amber">
              <i className="bi bi-pause-circle-fill"></i>
            </div>
            <div className="se-stat-content">
              <div className="se-stat-label">Inactifs</div>
              <div className="se-stat-value">{stats.inactifs}</div>
            </div>
          </div>
        </Col>
        <Col xs={6} md={4} lg={2}>
          <div className="se-stat-card">
            <div className="se-stat-icon-wrapper se-stat-icon-blue">
              <i className="bi bi-envelope-fill"></i>
            </div>
            <div className="se-stat-content">
              <div className="se-stat-label">Email</div>
              <div className="se-stat-value">{stats.email}</div>
            </div>
          </div>
        </Col>
        <Col xs={6} md={4} lg={2}>
          <div className="se-stat-card">
            <div className="se-stat-icon-wrapper se-stat-icon-green">
              <i className="bi bi-chat-dots-fill"></i>
            </div>
            <div className="se-stat-content">
              <div className="se-stat-label">SMS</div>
              <div className="se-stat-value">{stats.sms}</div>
            </div>
          </div>
        </Col>
        <Col xs={6} md={4} lg={2}>
          <div className="se-stat-card">
            <div className="se-stat-icon-wrapper se-stat-icon-gray">
              <i className="bi bi-bell-fill"></i>
            </div>
            <div className="se-stat-content">
              <div className="se-stat-label">Autres</div>
              <div className="se-stat-value">{stats.autresTypes}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="se-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={7} md={12}>
            <div className="se-search-wrapper">
              <i className="bi bi-search se-search-icon"></i>
              <input
                type="text"
                className="se-search-input"
                placeholder="Rechercher par ID marchand, seuil, type notification..."
                value={filterText}
                onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
              />
              {filterText && (
                <button className="se-search-clear" onClick={() => setFilterText('')}>
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="se-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={10}>10 par page</option>
              <option value={25}>25 par page</option>
              <option value={50}>50 par page</option>
              <option value={100}>100 par page</option>
            </select>
          </Col>
          <Col lg={2} md={6}>
            <button
              className="se-btn-secondary w-100"
              onClick={() => loadSeuils(true)}
              disabled={refreshing}
            >
              <i className={`bi ${refreshing ? 'bi-arrow-repeat se-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>Actualiser</span>
            </button>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="se-table-card">
        {loadingSeuils ? (
          <div className="se-loading">
            <div className="se-loading-spinner"></div>
            <div className="se-loading-text">Chargement des seuils...</div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="se-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <div className="se-checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="se-checkbox"
                          checked={isAllSelected}
                          ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    <SortableHeader field="marchand_id">
                      <i className="bi bi-shop me-1"></i>Marchand
                    </SortableHeader>
                    <SortableHeader field="seuil">
                      <i className="bi bi-graph-up-arrow me-1"></i>Seuil
                    </SortableHeader>
                    <SortableHeader field="type_notification">
                      <i className="bi bi-bell me-1"></i>Notification
                    </SortableHeader>
                    <SortableHeader field="actif">
                      <i className="bi bi-flag me-1"></i>Statut
                    </SortableHeader>
                    <SortableHeader field="date_creation">
                      <i className="bi bi-calendar me-1"></i>Date création
                    </SortableHeader>
                    <SortableHeader field="date_derniere_alerte">
                      <i className="bi bi-clock-history me-1"></i>Dernière alerte
                    </SortableHeader>
                    <th className="text-end" style={{ width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((seuil) => {
                      const isActif = seuil.actif === 1;
                      return (
                        <tr key={seuil.id} className="se-row">
                          <td>
                            <div className="se-checkbox-wrapper">
                              <input
                                type="checkbox"
                                className="se-checkbox"
                                checked={selectedSeuils.includes(seuil.id)}
                                onChange={() => handleSelectSeuil(seuil.id)}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="se-marchand-cell">
                              <div className="se-marchand-avatar">
                                M
                              </div>
                              <div className="se-marchand-content">
                                <span className="se-marchand-name">M-{seuil.marchand_id}</span>
                                <small className="se-marchand-id">ID: {seuil.id}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="se-seuil-value">
                              <i className="bi bi-coin"></i>
                              <span>{seuil.seuil}</span>
                              <small>crédits</small>
                            </div>
                          </td>
                          <td>
                            <span className={`se-notif-badge ${getTypeNotificationClass(seuil.type_notification)}`}>
                              <i className={`bi ${getTypeNotificationIcon(seuil.type_notification)}`}></i>
                              {getTypeNotificationLabel(seuil.type_notification)}
                            </span>
                          </td>
                          <td>
                            <span className={`se-status-badge ${getStatutClass(seuil.actif)}`}>
                              <i className={`bi ${isActif ? 'bi-check-circle-fill' : 'bi-pause-circle-fill'}`}></i>
                              {getStatutLabel(seuil.actif)}
                            </span>
                          </td>
                          <td>
                            <span className="se-date-cell">
                              <i className="bi bi-calendar"></i>
                              {formatDate(seuil.date_creation)}
                            </span>
                          </td>
                          <td>
                            {seuil.date_derniere_alerte ? (
                              <span className="se-date-cell se-date-alert">
                                <i className="bi bi-bell-fill"></i>
                                {formatDate(seuil.date_derniere_alerte)}
                              </span>
                            ) : (
                              <span className="se-date-cell se-date-none">
                                <i className="bi bi-dash-circle"></i>
                                Aucune
                              </span>
                            )}
                          </td>
                          <td className="text-end">
                            <div className="se-actions">
                              <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                <button
                                  className="se-action-btn se-action-view"
                                  onClick={() => handleShowDetails(seuil)}
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                              </OverlayTrigger>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="se-empty-state">
                          <div className="se-empty-icon">
                            <i className="bi bi-sliders2"></i>
                          </div>
                          <h5 className="se-empty-title">Aucun seuil configuré</h5>
                          <p className="se-empty-text">
                            {filterText
                              ? "Aucun seuil ne correspond à votre recherche"
                              : "Aucun seuil n'a encore été configuré"}
                          </p>
                          {filterText && (
                            <button className="se-btn-empty-reset" onClick={() => setFilterText('')}>
                              <i className="bi bi-arrow-clockwise me-2"></i>Réinitialiser
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {filteredAndSortedItems.length > 0 && totalPages > 1 && (
              <div className="se-pagination">
                <div className="se-pagination-info">
                  <i className="bi bi-info-circle"></i>
                  Affichage <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
                  {selectedSeuils.length > 0 && (
                    <span className="se-pagination-selected">
                      • <strong>{selectedSeuils.length}</strong> sélectionné{selectedSeuils.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="se-pagination-controls">
                  <button className="se-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button className="se-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                    <i className="bi bi-chevron-left"></i>
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const pageNum = currentPage <= 3
                      ? index + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + index
                        : currentPage - 2 + index;
                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          className={`se-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}
                  <button className="se-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button className="se-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ========== MODAL DÉTAILS ========== */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered className="se-modal">
        <div className="se-modal-header se-modal-header-info">
          <div className="d-flex align-items-center gap-3">
            <div className="se-modal-icon">
              <i className="bi bi-sliders2"></i>
            </div>
            <div>
              <h5 className="se-modal-title mb-0">Détails du seuil d'alerte</h5>
              <small className="se-modal-subtitle">Configuration complète</small>
            </div>
          </div>
          <button className="se-modal-close" onClick={() => setShowDetailsModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="se-modal-body">
          {selectedSeuil && (
            <>
              {/* HERO */}
              <div className="se-detail-hero">
                <div className="se-detail-avatar">
                  <i className="bi bi-sliders"></i>
                </div>
                <div className="se-detail-hero-content">
                  <div className="se-detail-hero-name">
                    Marchand M-{selectedSeuil.marchand_id}
                  </div>
                  <div className="se-detail-hero-meta">
                    <span><i className="bi bi-hash"></i>ID: {selectedSeuil.id}</span>
                    <span><i className="bi bi-coin"></i>{selectedSeuil.seuil} crédits</span>
                  </div>
                </div>
                <span className={`se-status-badge ${getStatutClass(selectedSeuil.actif)}`}>
                  <i className={`bi ${selectedSeuil.actif === 1 ? 'bi-check-circle-fill' : 'bi-pause-circle-fill'}`}></i>
                  {getStatutLabel(selectedSeuil.actif)}
                </span>
              </div>

              {/* SEUIL HIGHLIGHT */}
              <div className="se-detail-highlight">
                <div className="se-detail-highlight-icon">
                  <i className="bi bi-graph-up-arrow"></i>
                </div>
                <div className="se-detail-highlight-content">
                  <div className="se-detail-highlight-label">Seuil configuré</div>
                  <div className="se-detail-highlight-value">
                    {selectedSeuil.seuil} <span>crédits</span>
                  </div>
                </div>
              </div>

              {/* GRID */}
              <div className="se-detail-grid">
                <div className="se-detail-card">
                  <div className="se-detail-card-icon se-detail-card-icon-blue">
                    <i className={`bi ${getTypeNotificationIcon(selectedSeuil.type_notification)}`}></i>
                  </div>
                  <div className="se-detail-card-content">
                    <div className="se-detail-card-label">Type de notification</div>
                    <div className="se-detail-card-value">{getTypeNotificationLabel(selectedSeuil.type_notification)}</div>
                  </div>
                </div>

                <div className="se-detail-card">
                  <div className="se-detail-card-icon se-detail-card-icon-amber">
                    <i className="bi bi-calendar"></i>
                  </div>
                  <div className="se-detail-card-content">
                    <div className="se-detail-card-label">Date de création</div>
                    <div className="se-detail-card-value">
                      {selectedSeuil.date_creation ? new Date(selectedSeuil.date_creation).toLocaleDateString('fr-FR') : 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="se-detail-card">
                  <div className="se-detail-card-icon se-detail-card-icon-red">
                    <i className="bi bi-bell"></i>
                  </div>
                  <div className="se-detail-card-content">
                    <div className="se-detail-card-label">Dernière alerte</div>
                    <div className="se-detail-card-value">
                      {selectedSeuil.date_derniere_alerte ? new Date(selectedSeuil.date_derniere_alerte).toLocaleDateString('fr-FR') : 'Aucune'}
                    </div>
                  </div>
                </div>

                <div className="se-detail-card">
                  <div className="se-detail-card-icon se-detail-card-icon-green">
                    <i className="bi bi-arrow-repeat"></i>
                  </div>
                  <div className="se-detail-card-content">
                    <div className="se-detail-card-label">Dernière modification</div>
                    <div className="se-detail-card-value">
                      {selectedSeuil.updated_at ? new Date(selectedSeuil.updated_at).toLocaleDateString('fr-FR') : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              {/* INFO TECHNIQUE */}
              <div className="se-detail-section">
                <div className="se-detail-section-header">
                  <i className="bi bi-info-circle"></i>
                  <span>Informations techniques</span>
                </div>
                <div className="se-detail-section-body">
                  <div className="se-tech-row">
                    <span className="se-tech-label">Marchand ID</span>
                    <span className="se-tech-value">M-{selectedSeuil.marchand_id}</span>
                  </div>
                  <div className="se-tech-row">
                    <span className="se-tech-label">ID Seuil</span>
                    <span className="se-tech-value">{selectedSeuil.id}</span>
                  </div>
                  <div className="se-tech-row">
                    <span className="se-tech-label">Statut</span>
                    <span className="se-tech-value">
                      <span className={`se-status-badge ${getStatutClass(selectedSeuil.actif)}`}>
                        {getStatutLabel(selectedSeuil.actif)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <div className="se-modal-footer">
          <button className="se-btn-cancel" onClick={() => setShowDetailsModal(false)}>
            Fermer
          </button>
        </div>
      </Modal>

      {/* ========== MODAL ÉDITION ========== */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered className="se-modal">
        <div className="se-modal-header se-modal-header-blue">
          <div className="d-flex align-items-center gap-3">
            <div className="se-modal-icon">
              <i className="bi bi-pencil-square"></i>
            </div>
            <div>
              <h5 className="se-modal-title mb-0">Modifier le seuil</h5>
              <small className="se-modal-subtitle">Ajuster la configuration</small>
            </div>
          </div>
          <button className="se-modal-close" onClick={() => setShowEditModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="se-modal-body">
          <div className="se-form-group mb-3">
            <label className="se-form-label">
              Seuil (crédits) <span className="se-required">*</span>
            </label>
            <div className="se-input-wrapper">
              <i className="bi bi-coin se-input-icon"></i>
              <input
                type="number"
                step="0.01"
                className="se-input"
                value={editSeuil.seuil}
                onChange={(e) => setEditSeuil({ ...editSeuil, seuil: e.target.value })}
                placeholder="Ex: 2500.00"
              />
            </div>
            <div className="se-form-hint">
              <i className="bi bi-info-circle"></i>
              Définissez le seuil en crédits pour déclencher l'alerte
            </div>
          </div>

          <div className="se-form-group mb-3">
            <label className="se-form-label">Type de notification</label>
            <select
              className="se-select w-100"
              value={editSeuil.type_notification}
              onChange={(e) => setEditSeuil({ ...editSeuil, type_notification: e.target.value })}
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="push">Notification Push</option>
              <option value="both">Email et SMS</option>
            </select>
          </div>

          <div className="se-form-group">
            <div className="se-switch-wrapper">
              <label className="se-switch-label">
                <span className="se-switch-text">Activer la surveillance</span>
                <span className="se-switch">
                  <input
                    type="checkbox"
                    checked={editSeuil.actif === 1}
                    onChange={(e) => setEditSeuil({ ...editSeuil, actif: e.target.checked ? 1 : 0 })}
                  />
                  <span className="se-switch-slider"></span>
                </span>
              </label>
            </div>
          </div>
        </Modal.Body>

        <div className="se-modal-footer">
          <button className="se-btn-cancel" onClick={() => setShowEditModal(false)}>Annuler</button>
          <button
            className="se-btn-primary"
            onClick={handleUpdateSeuil}
            disabled={updateLoading || !editSeuil.seuil.trim()}
          >
            {updateLoading ? (
              <><Spinner animation="border" size="sm" /> Mise à jour...</>
            ) : (
              <><i className="bi bi-check-lg"></i> Mettre à jour</>
            )}
          </button>
        </div>
      </Modal>

      {/* ========== MODAL AJOUT ========== */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered className="se-modal">
        <div className="se-modal-header se-modal-header-success">
          <div className="d-flex align-items-center gap-3">
            <div className="se-modal-icon">
              <i className="bi bi-plus-circle-fill"></i>
            </div>
            <div>
              <h5 className="se-modal-title mb-0">Nouveau seuil d'alerte</h5>
              <small className="se-modal-subtitle">Configurer un seuil</small>
            </div>
          </div>
          <button className="se-modal-close" onClick={() => setShowAddModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="se-modal-body">
          <div className="se-form-group mb-3">
            <label className="se-form-label">
              Marchand ID <span className="se-required">*</span>
            </label>
            <div className="se-input-wrapper">
              <i className="bi bi-shop se-input-icon"></i>
              <input
                type="number"
                className="se-input"
                placeholder="Ex: 1003"
              />
            </div>
            <div className="se-form-hint">
              <i className="bi bi-info-circle"></i>
              Identifiant du marchand pour lequel configurer le seuil
            </div>
          </div>

          <div className="se-form-group mb-3">
            <label className="se-form-label">
              Seuil (crédits) <span className="se-required">*</span>
            </label>
            <div className="se-input-wrapper">
              <i className="bi bi-coin se-input-icon"></i>
              <input
                type="number"
                step="0.01"
                className="se-input"
                value={newSeuil.seuil}
                onChange={(e) => setNewSeuil({ ...newSeuil, seuil: e.target.value })}
                placeholder="Ex: 2500.00"
              />
            </div>
          </div>

          <div className="se-form-group mb-3">
            <label className="se-form-label">Type de notification</label>
            <select
              className="se-select w-100"
              value={newSeuil.type_notification}
              onChange={(e) => setNewSeuil({ ...newSeuil, type_notification: e.target.value })}
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="push">Notification Push</option>
              <option value="both">Email et SMS</option>
            </select>
          </div>

          <div className="se-form-group">
            <div className="se-switch-wrapper">
              <label className="se-switch-label">
                <span className="se-switch-text">Activer immédiatement</span>
                <span className="se-switch">
                  <input
                    type="checkbox"
                    checked={newSeuil.actif === 1}
                    onChange={(e) => setNewSeuil({ ...newSeuil, actif: e.target.checked ? 1 : 0 })}
                  />
                  <span className="se-switch-slider"></span>
                </span>
              </label>
            </div>
          </div>
        </Modal.Body>

        <div className="se-modal-footer">
          <button className="se-btn-cancel" onClick={() => setShowAddModal(false)}>Annuler</button>
          <button
            className="se-btn-success"
            onClick={handleCreateSeuil}
            disabled={createLoading || !newSeuil.seuil.trim()}
          >
            {createLoading ? (
              <><Spinner animation="border" size="sm" /> Création...</>
            ) : (
              <><i className="bi bi-check-lg"></i> Créer le seuil</>
            )}
          </button>
        </div>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .se-page { background: #f8fafc; min-height: 100vh; }
        .se-page * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

        /* ========== HEADER ========== */
        .se-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px; padding: 24px 28px;
          border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .se-header-icon {
          width: 56px; height: 56px; border-radius: 14px;
          background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 26px; box-shadow: 0 8px 20px -6px rgba(139, 92, 246, 0.5);
        }
        .se-header-title {
          font-weight: 700; color: #0f172a; font-size: 1.5rem; letter-spacing: -0.02em;
        }
        .se-header-subtitle { color: #64748b; font-size: 0.875rem; }

        /* ========== BUTTONS ========== */
        .se-btn-primary, .se-btn-secondary, .se-btn-cancel, .se-btn-success {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          padding: 11px 20px; border-radius: 10px; font-weight: 600; font-size: 0.875rem;
          border: none; transition: all 0.2s; cursor: pointer; text-decoration: none;
        }
        .se-btn-primary {
          background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%); color: white;
        }
        .se-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%);
          transform: translateY(-1px); box-shadow: 0 8px 16px -4px rgba(139, 92, 246, 0.4);
        }
        .se-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .se-btn-secondary {
          background: white; border: 1px solid #e2e8f0; color: #334155;
        }
        .se-btn-secondary:hover:not(:disabled) {
          background: #f8fafc; border-color: #cbd5e1; color: #0f172a;
        }
        .se-btn-secondary:disabled { opacity: 0.6; }
        .se-btn-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white;
        }
        .se-btn-success:hover:not(:disabled) {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          transform: translateY(-1px); box-shadow: 0 8px 16px -4px rgba(16, 185, 129, 0.4);
        }
        .se-btn-cancel {
          background: white; border: 1px solid #e2e8f0; color: #334155;
        }
        .se-btn-cancel:hover { background: #f1f5f9; }
        .se-dropdown-menu {
          border: 1px solid #e2e8f0; border-radius: 10px;
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.1); padding: 6px;
        }
        .se-dropdown-menu .dropdown-item {
          border-radius: 6px; padding: 8px 12px; font-size: 0.85rem; display: flex; align-items: center;
        }
        .se-dropdown-menu .dropdown-item:hover { background: #f1f5f9; }

        /* ========== ALERTS ========== */
        .se-alert {
          display: flex; align-items: center; gap: 12px; padding: 14px 18px;
          border-radius: 12px; font-size: 0.85rem; font-weight: 500;
        }
        .se-alert > i { font-size: 1.3rem; flex-shrink: 0; }
        .se-alert-success { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); color: #065f46; border-left: 4px solid #10b981; }
        .se-alert-danger { background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); color: #991b1b; border-left: 4px solid #ef4444; }
        .se-alert-warning { background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); color: #92400e; border-left: 4px solid #f59e0b; }
        .se-alert-info { background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); color: #1e40af; border-left: 4px solid #3b82f6; }
        .se-alert-close {
          background: none; border: none; color: inherit; opacity: 0.6; cursor: pointer;
          padding: 4px 8px; border-radius: 6px; font-size: 1rem;
        }
        .se-alert-close:hover { opacity: 1; background: rgba(0,0,0,0.05); }

        /* ========== STATS ========== */
        .se-stat-card {
          display: flex; align-items: center; gap: 12px; background: white;
          border-radius: 14px; padding: 16px; border: 1px solid #e2e8f0;
          transition: all 0.25s; height: 100%;
        }
        .se-stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08); }
        .se-stat-icon-wrapper {
          width: 42px; height: 42px; border-radius: 11px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; flex-shrink: 0;
        }
        .se-stat-icon-blue  { background: #ede9fe; color: #8b5cf6; }
        .se-stat-icon-green { background: #d1fae5; color: #10b981; }
        .se-stat-icon-amber { background: #fef3c7; color: #d97706; }
        .se-stat-icon-gray  { background: #f1f5f9; color: #64748b; }
        .se-stat-content { min-width: 0; flex-grow: 1; }
        .se-stat-label {
          font-size: 0.65rem; color: #64748b; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 3px;
        }
        .se-stat-value {
          font-size: 1.35rem; font-weight: 700; color: #0f172a;
          letter-spacing: -0.02em; line-height: 1;
        }

        /* ========== FILTRES ========== */
        .se-filters {
          background: white; border-radius: 14px; padding: 16px 20px; border: 1px solid #e2e8f0;
        }
        .se-search-wrapper { position: relative; display: flex; align-items: center; }
        .se-search-icon {
          position: absolute; left: 14px; color: #94a3b8; font-size: 1.05rem; pointer-events: none;
        }
        .se-search-input {
          width: 100%; padding: 11px 40px 11px 42px; border: 1px solid #e2e8f0;
          border-radius: 10px; font-size: 0.875rem; background: #f8fafc;
          transition: all 0.2s; outline: none; font-family: inherit;
        }
        .se-search-input:focus {
          background: white; border-color: #8b5cf6; box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }
        .se-search-clear {
          position: absolute; right: 8px; background: none; border: none; color: #94a3b8;
          font-size: 1rem; padding: 6px 10px; cursor: pointer; border-radius: 6px; transition: all 0.15s;
        }
        .se-search-clear:hover { background: #fee2e2; color: #ef4444; }
        .se-select {
          width: 100%; padding: 11px 14px; border: 1px solid #e2e8f0;
          border-radius: 10px; font-size: 0.875rem; font-weight: 500;
          background: #f8fafc; color: #334155; outline: none; cursor: pointer; font-family: inherit;
        }
        .se-select:focus {
          background: white; border-color: #8b5cf6; box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        /* ========== TABLE ========== */
        .se-table-card {
          background: white; border-radius: 16px; border: 1px solid #e2e8f0;
          overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .se-table { font-size: 0.875rem; }
        .se-table thead th {
          background: #f8fafc; color: #475569; font-weight: 700; font-size: 0.7rem;
          text-transform: uppercase; letter-spacing: 0.05em; padding: 16px;
          border-bottom: 1px solid #e2e8f0; border-top: none; white-space: nowrap;
        }
        .se-table tbody td {
          padding: 16px; vertical-align: middle; border-bottom: 1px solid #f1f5f9;
        }
        .se-row { transition: background-color 0.15s; }
        .se-row:hover { background-color: #f8fafc; }
        .se-sortable-header { cursor: pointer; user-select: none; }
        .se-sortable-header:hover { color: #8b5cf6; }
        .se-sortable-content { display: inline-flex; align-items: center; gap: 6px; }
        .se-sort-icon { color: #8b5cf6; font-size: 0.75rem; }

        .se-checkbox-wrapper { display: flex; align-items: center; justify-content: center; }
        .se-checkbox {
          width: 18px; height: 18px; border-radius: 5px; border: 1.5px solid #cbd5e1;
          cursor: pointer; accent-color: #8b5cf6;
        }

        /* Marchand cell */
        .se-marchand-cell { display: flex; align-items: center; gap: 12px; }
        .se-marchand-avatar {
          width: 38px; height: 38px; border-radius: 10px;
          background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 14px; flex-shrink: 0;
        }
        .se-marchand-content { min-width: 0; }
        .se-marchand-name { font-weight: 700; color: #0f172a; display: block; }
        .se-marchand-id { color: #94a3b8; font-size: 0.7rem; font-family: 'SF Mono', monospace; }

        /* Seuil value */
        .se-seuil-value {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 11px; background: #ede9fe;
          border-radius: 6px; color: #5b21b6;
          font-weight: 700; font-size: 0.85rem;
        }
        .se-seuil-value i { font-size: 0.9rem; }
        .se-seuil-value small {
          font-size: 0.65rem; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.03em;
          opacity: 0.7;
        }

        /* Notification badge */
        .se-notif-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 11px; border-radius: 6px;
          font-size: 0.7rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.03em;
        }
        .se-notif-email   { background: #dbeafe; color: #1e40af; }
        .se-notif-sms     { background: #d1fae5; color: #065f46; }
        .se-notif-push    { background: #fef3c7; color: #92400e; }
        .se-notif-both    { background: #cffafe; color: #0e7490; }
        .se-notif-default { background: #f1f5f9; color: #64748b; }

        /* Status badge */
        .se-status-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 11px; border-radius: 6px;
          font-weight: 700; font-size: 0.72rem; white-space: nowrap;
        }
        .se-status-active { background: #d1fae5; color: #059669; }
        .se-status-inactive { background: #fee2e2; color: #b91c1c; }

        /* Date cell */
        .se-date-cell {
          display: inline-flex; align-items: center; gap: 6px;
          color: #64748b; font-size: 0.8rem; font-variant-numeric: tabular-nums;
        }
        .se-date-cell i { color: #94a3b8; }
        .se-date-alert { color: #d97706; }
        .se-date-alert i { color: #f59e0b; }
        .se-date-none { color: #cbd5e1; font-style: italic; }
        .se-date-none i { color: #cbd5e1; }

        /* Actions */
        .se-actions { display: inline-flex; gap: 6px; justify-content: flex-end; }
        .se-action-btn {
          width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e2e8f0;
          background: white; color: #64748b; display: inline-flex;
          align-items: center; justify-content: center; cursor: pointer;
          transition: all 0.15s; font-size: 0.9rem;
        }
        .se-action-view:hover {
          background: #8b5cf6; border-color: #8b5cf6; color: white;
          transform: translateY(-1px); box-shadow: 0 4px 12px -2px rgba(139, 92, 246, 0.4);
        }

        /* ========== LOADING / EMPTY ========== */
        .se-loading { padding: 80px 20px; text-align: center; }
        .se-loading-spinner {
          width: 48px; height: 48px; border: 4px solid #e2e8f0;
          border-top-color: #8b5cf6; border-radius: 50%;
          animation: se-spin 0.8s linear infinite; margin: 0 auto 16px;
        }
        .se-loading-text { color: #64748b; font-size: 0.875rem; font-weight: 500; }
        .se-empty-state { padding: 60px 20px; text-align: center; }
        .se-empty-icon {
          width: 80px; height: 80px; border-radius: 50%; background: #f1f5f9; color: #94a3b8;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 36px; margin-bottom: 20px;
        }
        .se-empty-title { color: #334155; font-weight: 700; margin-bottom: 8px; }
        .se-empty-text { color: #94a3b8; margin-bottom: 20px; }
        .se-btn-empty-reset {
          display: inline-flex; align-items: center; background: white; color: #8b5cf6;
          border: 1px solid #ddd6fe; border-radius: 8px; font-weight: 600;
          padding: 8px 16px; cursor: pointer; transition: all 0.2s;
        }
        .se-btn-empty-reset:hover { background: #8b5cf6; color: white; border-color: #8b5cf6; }

        /* ========== PAGINATION ========== */
        .se-pagination {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 24px; background: #f8fafc; border-top: 1px solid #e2e8f0;
          flex-wrap: wrap; gap: 12px;
        }
        .se-pagination-info {
          font-size: 0.8rem; color: #64748b; display: flex; align-items: center; gap: 6px;
        }
        .se-pagination-info strong { color: #0f172a; }
        .se-pagination-selected { color: #8b5cf6; margin-left: 8px; }
        .se-pagination-controls { display: flex; align-items: center; gap: 6px; }
        .se-page-btn {
          min-width: 34px; height: 34px; padding: 0 10px; border-radius: 8px;
          border: 1px solid #e2e8f0; background: white; color: #475569;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.15s; font-size: 0.8rem; font-weight: 600;
        }
        .se-page-btn:hover:not(:disabled) { background: #8b5cf6; color: white; border-color: #8b5cf6; }
        .se-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .se-page-btn.active { background: #8b5cf6; color: white; border-color: #8b5cf6; }

        /* ========== MODAL ========== */
        .se-modal .modal-content { border: none; border-radius: 16px; overflow: hidden; }
        .se-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 24px; color: white;
        }
        .se-modal-header-info { background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%); }
        .se-modal-header-blue { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); }
        .se-modal-header-success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
        .se-modal-icon {
          width: 44px; height: 44px; border-radius: 10px; background: rgba(255,255,255,0.2);
          display: flex; align-items: center; justify-content: center; font-size: 20px;
        }
        .se-modal-title { font-weight: 700; letter-spacing: -0.01em; margin: 0; }
        .se-modal-subtitle { color: rgba(255,255,255,0.85); font-size: 0.8rem; }
        .se-modal-close {
          background: rgba(255,255,255,0.1); border: none; color: white;
          width: 36px; height: 36px; border-radius: 10px; display: flex;
          align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 1rem;
        }
        .se-modal-close:hover { background: rgba(255,255,255,0.2); transform: rotate(90deg); }
        .se-modal-body {
          padding: 24px; background: #f8fafc; max-height: 70vh; overflow-y: auto;
        }
        .se-modal-footer {
          padding: 16px 24px; background: white; border-top: 1px solid #e2e8f0;
          display: flex; justify-content: flex-end; gap: 10px;
        }

        /* Detail Hero */
        .se-detail-hero {
          display: flex; align-items: center; gap: 16px; padding: 20px;
          background: white; border-radius: 14px; border: 1px solid #e2e8f0;
          margin-bottom: 16px; flex-wrap: wrap;
        }
        .se-detail-avatar {
          width: 60px; height: 60px; border-radius: 16px;
          background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 24px; flex-shrink: 0;
          box-shadow: 0 8px 16px -4px rgba(139, 92, 246, 0.3);
        }
        .se-detail-hero-content { flex-grow: 1; min-width: 0; }
        .se-detail-hero-name {
          font-weight: 700; color: #0f172a; font-size: 1.05rem; margin-bottom: 6px;
        }
        .se-detail-hero-meta {
          display: flex; flex-wrap: wrap; gap: 14px; font-size: 0.8rem; color: #64748b;
        }
        .se-detail-hero-meta span { display: inline-flex; align-items: center; gap: 5px; }
        .se-detail-hero-meta i { color: #94a3b8; }

        /* Highlight seuil */
        .se-detail-highlight {
          display: flex; align-items: center; gap: 16px;
          padding: 20px; border-radius: 14px;
          background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
          border: 1px solid #c4b5fd;
          margin-bottom: 16px;
        }
        .se-detail-highlight-icon {
          width: 52px; height: 52px; border-radius: 14px;
          background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 22px; flex-shrink: 0;
          box-shadow: 0 8px 16px -4px rgba(139, 92, 246, 0.4);
        }
        .se-detail-highlight-label {
          font-size: 0.7rem; color: #6d28d9; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;
        }
        .se-detail-highlight-value {
          font-size: 1.6rem; font-weight: 700; color: #5b21b6;
          letter-spacing: -0.02em; line-height: 1;
        }
        .se-detail-highlight-value span {
          font-size: 0.85rem; font-weight: 600; color: #7c3aed; margin-left: 4px;
        }

        /* Detail Grid */
        .se-detail-grid {
          display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 16px;
        }
        .se-detail-card {
          display: flex; align-items: center; gap: 14px; padding: 16px;
          background: white; border-radius: 12px; border: 1px solid #e2e8f0;
          transition: all 0.2s;
        }
        .se-detail-card:hover { transform: translateY(-2px); box-shadow: 0 8px 16px -6px rgba(0, 0, 0, 0.08); }
        .se-detail-card-icon {
          width: 42px; height: 42px; border-radius: 10px; display: flex;
          align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0;
        }
        .se-detail-card-icon-blue  { background: #dbeafe; color: #3b82f6; }
        .se-detail-card-icon-amber { background: #fef3c7; color: #d97706; }
        .se-detail-card-icon-red   { background: #fee2e2; color: #ef4444; }
        .se-detail-card-icon-green { background: #d1fae5; color: #059669; }
        .se-detail-card-content { flex-grow: 1; min-width: 0; }
        .se-detail-card-label {
          font-size: 0.68rem; color: #94a3b8; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;
        }
        .se-detail-card-value { font-weight: 700; color: #0f172a; font-size: 0.85rem; }

        /* Detail Section */
        .se-detail-section {
          background: white; border-radius: 12px; border: 1px solid #e2e8f0; overflow: hidden;
        }
        .se-detail-section-header {
          display: flex; align-items: center; gap: 8px; padding: 14px 18px;
          background: #f8fafc; border-bottom: 1px solid #e2e8f0;
          font-weight: 700; color: #0f172a; font-size: 0.85rem;
        }
        .se-detail-section-header i { color: #8b5cf6; font-size: 1rem; }
        .se-detail-section-body { padding: 16px 18px; }
        .se-tech-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 10px 0; border-bottom: 1px solid #f1f5f9;
        }
        .se-tech-row:last-child { border-bottom: none; }
        .se-tech-label {
          font-size: 0.78rem; color: #94a3b8; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.03em;
        }
        .se-tech-value {
          font-size: 0.85rem; color: #0f172a; font-weight: 600;
        }

        /* Form */
        .se-form-group { margin-bottom: 4px; }
        .se-form-label {
          display: block; font-weight: 700; color: #0f172a; font-size: 0.82rem; margin-bottom: 8px;
        }
        .se-required { color: #ef4444; }
        .se-input-wrapper { position: relative; display: flex; align-items: center; }
        .se-input-icon {
          position: absolute; left: 14px; color: #94a3b8; font-size: 1rem; pointer-events: none;
        }
        .se-input {
          width: 100%; padding: 11px 14px 11px 42px; border: 2px solid #e2e8f0;
          border-radius: 10px; font-size: 0.875rem; color: #0f172a; background: white;
          transition: all 0.2s; outline: none; font-family: inherit;
        }
        .se-input:focus { border-color: #8b5cf6; box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.1); }
        .se-form-hint {
          margin-top: 6px; font-size: 0.72rem; color: #94a3b8;
          display: flex; align-items: center; gap: 4px;
        }
        .se-form-hint i { color: #8b5cf6; }

        /* Switch */
        .se-switch-wrapper {
          padding: 12px 16px; background: #f8fafc;
          border-radius: 10px; border: 1px solid #e2e8f0;
        }
        .se-switch-label {
          display: flex; justify-content: space-between; align-items: center;
          cursor: pointer; margin: 0;
        }
        .se-switch-text {
          font-weight: 600; color: #0f172a; font-size: 0.85rem;
        }
        .se-switch {
          position: relative; display: inline-block; width: 46px; height: 24px;
        }
        .se-switch input { opacity: 0; width: 0; height: 0; }
        .se-switch-slider {
          position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
          background: #cbd5e1; border-radius: 24px; transition: 0.3s;
        }
        .se-switch-slider:before {
          position: absolute; content: ""; height: 18px; width: 18px;
          left: 3px; bottom: 3px; background: white; border-radius: 50%;
          transition: 0.3s; box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .se-switch input:checked + .se-switch-slider {
          background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
        }
        .se-switch input:checked + .se-switch-slider:before {
          transform: translateX(22px);
        }

        /* ========== ANIMATIONS ========== */
        .se-spin { animation: se-spin 1s linear infinite; display: inline-block; }
        @keyframes se-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 992px) {
          .se-detail-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .se-header { padding: 20px; }
          .se-header-title { font-size: 1.2rem; }
          .se-pagination { flex-direction: column; }
          .se-stat-value { font-size: 1.15rem; }
          .se-stat-icon-wrapper { width: 36px; height: 36px; font-size: 16px; }
          .se-modal-body { padding: 16px; }
          .se-detail-hero { padding: 16px; }
          .se-detail-avatar { width: 52px; height: 52px; font-size: 20px; }
        }
      `}</style>
    </div>
  );
}